/* FFC Personalisation — real WebGL 3D T-shirt preview (Three.js), replacing
   the earlier inline-SVG stage. Adapted from a reference prototype the user
   supplied (F:\flexifit_3d_tshirt_personalisation_v3.html) and re-exposed
   here as a small bridge (window.FFCPersonalisation3D) so ffc-personalisation
   .js can drive it without owning any Three.js code itself -- same pattern
   as the FFCPersonalisation bridge used by the product configurator.

   IMPORTANT / NOT YET VISUALLY VERIFIED: this module was written and
   syntax-checked but never opened in a real browser (no GPU/browser access
   in the environment that authored it). Two things specifically need a
   live visual pass before this ships:
   1. MODEL_URL below is a third-party generic crew-neck tee GLB (not a
      FlexiFit product scan) -- fine for validating the mechanism, wrong
      long-term. Swap for a self-hosted FlexiFit garment model later.
   2. Decal placement calibration (ZONES below) was copied verbatim from
      the reference file's own working values for that exact model -- see
      the "identity matrix" comment in showDecal() for why this should
      still be correct regardless of when it runs, but it has not been
      confirmed on-screen. If a logo lands in the wrong spot, this is the
      first place to check.
*/
(function () {
  'use strict';

  var MODEL_URL = 'https://raw.githubusercontent.com/adrianhajdin/project_threejs_ai/main/client/public/shirt_baked.glb';

  // Calibrated against MODEL_URL's own native (unscaled) proportions --
  // position/rotation/size are in the mesh's local space. Keys match the
  // placement ids used by ffc-personalisation.js's PLACEMENTS array
  // (left_chest/right_chest/left_sleeve/right_sleeve/neck/back), so that
  // file can pass its own placement id straight through without a lookup
  // table living in two places.
  var ZONES = {
    // Chest raised from the reference file's 0.055 to 0.095 per owner
    // feedback ("logo na klatce powinno się instalować trochę wyżej").
    // Reference points on this model: back sits at 0.070, neck at 0.180 --
    // so 0.095 is a real lift off mid-chest without climbing onto the
    // shoulder/collar.
    // Chest artwork is rendered on a shallow plane just above the fabric.
    // DecalGeometry clips against the model triangles and, on this low-poly
    // sample shirt, that cut the top corners of otherwise complete logos into
    // a curved/semicircular shape. A front-facing plane preserves the entire
    // uploaded artwork while remaining attached to the rotating garment.
    left_chest: { pos: [0.075, 0.095, 0.145], rot: [0, 0, 0], size: [0.075, 0.075, 0.075], render: 'plane', surfaceOffset: 0.008 },
    right_chest: { pos: [-0.075, 0.095, 0.145], rot: [0, 0, 0], size: [0.075, 0.075, 0.075], render: 'plane', surfaceOffset: 0.008 },
    // Recalibrated 2026-08-16 against the rendered model (owner reported the
    // logo landing on the shoulder cap / sliding off the silhouette edge).
    // The old `rot` was a near-frontal lookAt, so the projector box pushed
    // the decal onto the FRONT face of the sleeve -- visible head-on, but
    // sliding round the edge exactly where the sleeve is actually viewed.
    // A sleeve is viewed from the SIDE: at that quarter turn the camera
    // looks along local ±X, so the projector's own Z axis has to point that
    // way too (rot.y = ±90 degrees) and the box sits just outside the arm
    // surface (|x| raised, z centred on the arm rather than offset toward
    // the front).
    // z is the front-back axis (back of the garment is negative z, cf. the
    // `back` zone at -0.125), so the small negative z shifts both sleeve
    // logos slightly rearward on the arm per owner feedback -- same value on
    // both sleeves, since this axis is not the mirrored one.
    left_sleeve: { pos: [0.235, 0.0731, -0.022], rot: [0, Math.PI / 2, 0], size: [0.075, 0.075, 0.07] },
    right_sleeve: { pos: [-0.235, 0.0731, -0.022], rot: [0, -Math.PI / 2, 0], size: [0.075, 0.075, 0.07] },
    // size[1] is the decal's UNIT height -- showDecal multiplies it by the
    // artwork's aspect ratio, so for it to leave a square logo square it has
    // to equal size[0]. Neck was 0.075 x 0.045, which squashed every square
    // logo into a letterbox. Width follows the same cm-to-local scale the
    // chest uses (0.075 for a 10.5 cm print), so an 8 cm neck print is
    // 0.075 * 8/10.5 = 0.057.
    neck: { pos: [0, 0.192, -0.118], rot: [0, Math.PI, 0], size: [0.057, 0.057, 0.05] },
    // Print area is LANDSCAPE A4 (29.7 x 21 cm) per owner instruction,
    // expressed in the same cm-to-local scale the chest uses (0.075 for a
    // 10.5 cm print => 0.00714 per cm): 0.212 x 0.150. Artwork is fitted
    // inside this, never stretched to it (see showDecal). Height sits
    // between chest (0.095) and neck (0.192) -- upper back, below the
    // collar seam rather than starting at it.
    back: { pos: [0, 0.070, -0.125], rot: [0, Math.PI, 0], size: [0.212, 0.150, 0.09] }
  };

  var THREE = null, GLTFLoader = null, DecalGeometry = null;
  var s = {
    canvas: null,
    renderer: null,
    scene: null,
    camera: null,
    root: null,
    shirtMesh: null,
    loading: false,
    ready: false,
    failed: false,
    active: false,
    rafId: null,
    colour: '#9a978c',
    rotationDeg: 0,
    decals: {}, // placementId -> THREE.Mesh
    readyCallbacks: [] // (err|null) => void
  };

  function flushReadyCallbacks(err) {
    var cbs = s.readyCallbacks;
    s.readyCallbacks = [];
    cbs.forEach(function (cb) { try { cb(err || null); } catch (e2) { /* noop */ } });
  }

  function applyRotation() {
    if (s.root) s.root.rotation.y = THREE.MathUtils.degToRad(s.rotationDeg);
  }

  function resize() {
    if (!s.canvas || !s.renderer) return;
    var r = s.canvas.getBoundingClientRect();
    if (!r.width || !r.height) return; // panel hidden -- skip, avoids NaN aspect
    var ratio = s.renderer.getPixelRatio();
    var w = Math.max(1, Math.round(r.width * ratio));
    var h = Math.max(1, Math.round(r.height * ratio));
    if (s.canvas.width !== w || s.canvas.height !== h) {
      s.renderer.setSize(r.width, r.height, false);
      s.camera.aspect = r.width / r.height;
      s.camera.updateProjectionMatrix();
    }
  }

  function loop() {
    if (!s.active) { s.rafId = null; return; }
    resize();
    s.renderer.render(s.scene, s.camera);
    s.rafId = requestAnimationFrame(loop);
  }

  function loadModel() {
    new GLTFLoader().load(
      MODEL_URL,
      function (gltf) {
        var firstMesh = null;
        gltf.scene.traverse(function (o) { if (o.isMesh && !firstMesh) firstMesh = o; });
        if (!firstMesh) { s.failed = true; s.loading = false; flushReadyCallbacks(new Error('Model contains no mesh')); return; }

        s.shirtMesh = firstMesh;
        s.shirtMesh.material = s.shirtMesh.material.clone();
        s.shirtMesh.material.color.set(s.colour);
        s.shirtMesh.material.roughness = 0.86;
        if ('metalness' in s.shirtMesh.material) s.shirtMesh.material.metalness = 0;

        s.root.add(gltf.scene);

        var box = new THREE.Box3().setFromObject(gltf.scene);
        var size = new THREE.Vector3(); var center = new THREE.Vector3();
        box.getSize(size); box.getCenter(center);
        // Target on-screen height, in the same local units the ZONES table's
        // pos/rot/size are calibrated against. This only scales what's
        // visible in the viewport -- decal placement is computed against
        // the mesh's identity-matrix local space (see showDecal) regardless
        // of this value, so it's safe to tune independently.
        // Was 4.65, then 4.185. Reduced again to 3.05: at 4.185 the garment
        // measured y 0-558 in a 559px canvas, i.e. it ran off the top and
        // bottom of the frame entirely. This leaves real margin around the
        // model -- needed both so the shopper sees the whole garment and so
        // there is any scenery visible behind it.
        var TARGET_HEIGHT = 3.05;
        var scale = TARGET_HEIGHT / (size.y || 1);
        gltf.scene.scale.setScalar(scale);
        gltf.scene.position.set(-center.x * scale, -center.y * scale - 0.05, -center.z * scale);

        applyRotation();
        s.ready = true;
        s.loading = false;
        flushReadyCallbacks(null);
      },
      undefined,
      function (err) {
        window.console && console.warn('FFC 3D preview: model failed to load', err);
        s.failed = true;
        s.loading = false;
        flushReadyCallbacks(err instanceof Error ? err : new Error('Model load failed'));
      }
    );
  }

  function ensureScene() {
    if (s.renderer || s.loading || s.failed) return;
    s.canvas = document.querySelector('[data-fp-viewer]');
    if (!s.canvas || !THREE) return;
    s.loading = true;

    try {
      s.renderer = new THREE.WebGLRenderer({ canvas: s.canvas, antialias: true, alpha: true, preserveDrawingBuffer: true });
    } catch (e) {
      s.failed = true; s.loading = false; flushReadyCallbacks(e); return;
    }
    s.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    s.renderer.outputColorSpace = THREE.SRGBColorSpace;
    s.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    s.renderer.toneMappingExposure = 1.12;

    s.scene = new THREE.Scene();
    s.camera = new THREE.PerspectiveCamera(31, 1, 0.01, 100);
    s.camera.position.set(0, 0.10, 7.4);

    s.scene.add(new THREE.HemisphereLight(0xffffff, 0x6e7d91, 2.3));
    var key = new THREE.DirectionalLight(0xffffff, 4.4); key.position.set(4.3, 5.4, 6.0); s.scene.add(key);
    var fillLight = new THREE.DirectionalLight(0xcfe4ff, 2.0); fillLight.position.set(-4.0, 2.4, 4.0); s.scene.add(fillLight);
    var rim = new THREE.DirectionalLight(0xffffff, 2.8); rim.position.set(1.5, 3.0, -4.2); s.scene.add(rim);

    s.root = new THREE.Group();
    s.scene.add(s.root);

    window.addEventListener('resize', resize);
    loadModel();
  }

  function clearDecal(id) {
    var mesh = s.decals[id];
    if (!mesh) return;
    if (s.shirtMesh) s.shirtMesh.remove(mesh);
    if (mesh.geometry) mesh.geometry.dispose();
    if (mesh.material) {
      if (mesh.material.map) mesh.material.map.dispose();
      mesh.material.dispose();
    }
    delete s.decals[id];
  }

  /* Loads ES modules by bare specifier (resolved through the page's import
     map) WITHOUT a literal `import()` in this file.

     Why the indirection: Shopify's theme-asset pipeline runs .js assets
     through esbuild in CommonJS mode, which rewrites a literal
     `import('three')` into `__toESM(require("three"))`. `require` does not
     exist in a browser, so the load rejected with "require is not defined"
     and the preview silently sat in its failed state -- the model never
     appeared. Confirmed by fetching the *served* asset from the CDN and
     reading it: the minified copy really does contain `require("three")`.

     An injected inline `<script type="module">` puts the import() in code
     esbuild never sees, and inline modules honour the page's import map
     exactly like the static one would have. Verified live in the browser
     before shipping. Do NOT "simplify" this back to a bare import(). */
  function importByMap(specifiers) {
    return new Promise(function (resolve, reject) {
      var key = '__ffcImport' + Date.now() + Math.floor(Math.random() * 1e6);
      var done = false;
      var settle = function (fn, v) { if (done) return; done = true; delete window[key]; fn(v); };
      window[key] = {
        resolve: function (v) { settle(resolve, v); },
        reject: function (e) { settle(reject, e); }
      };
      var el = document.createElement('script');
      el.type = 'module';
      el.textContent =
        'const h = window[' + JSON.stringify(key) + '];\n' +
        'try { h.resolve(await Promise.all([' +
        specifiers.map(function (spec) { return 'import(' + JSON.stringify(spec) + ')'; }).join(', ') +
        '])); } catch (e) { h.reject(e); }';
      // A module that fails to *parse* never runs either handler, so don't
      // leave the caller waiting on a promise that can never settle.
      setTimeout(function () { settle(reject, new Error('module load timed out')); }, 30000);
      document.head.appendChild(el);
    });
  }

  var API = {
    // Loads the three.js/GLTF/DecalGeometry modules and the GLB the first
    // time the personalisation panel is actually opened -- never on plain
    // page load, so shoppers who never personalise never pay for it.
    activate: function () {
      s.active = true;
      if (THREE) { ensureScene(); if (!s.rafId) loop(); return; }
      importByMap([
        'three',
        'three/addons/loaders/GLTFLoader.js',
        'three/addons/geometries/DecalGeometry.js'
      ]).then(function (mods) {
        THREE = mods[0];
        GLTFLoader = mods[1].GLTFLoader;
        DecalGeometry = mods[2].DecalGeometry;
        ensureScene();
        if (s.active && !s.rafId) loop();
      }).catch(function (err) {
        window.console && console.warn('FFC 3D preview: failed to load three.js', err);
        s.failed = true;
        flushReadyCallbacks(err);
      });
    },
    deactivate: function () { s.active = false; },
    isFailed: function () { return s.failed; },
    onReady: function (cb) {
      if (s.ready) { cb(null); return; }
      if (s.failed) { cb(new Error('3D preview unavailable')); return; }
      s.readyCallbacks.push(cb);
    },
    setColour: function (hex) {
      s.colour = hex;
      if (s.shirtMesh && s.shirtMesh.material) s.shirtMesh.material.color.set(hex);
    },
    setRotation: function (deg) {
      s.rotationDeg = deg;
      applyRotation();
    },
    // `placementId` matches PLACEMENTS ids in ffc-personalisation.js.
    // `offsetX`/`offsetY` are the same -8..8 nudge units used by the 2D
    // stage; approximated here as a small world-space shift since a true
    // screen-space nudge would need the zone's own local right/up basis.
    showDecal: function (placementId, imageUrl, aspect, offsetX, offsetY) {
      if (!s.shirtMesh || !s.ready) return;
      var zone = ZONES[placementId];
      if (!zone) return;
      clearDecal(placementId);

      var NUDGE_UNIT = 0.0015;
      var pos = new THREE.Vector3(
        zone.pos[0] + (offsetX || 0) * NUDGE_UNIT,
        zone.pos[1] + (offsetY || 0) * -NUDGE_UNIT, // screen "up" nudge = +Y in this space
        zone.pos[2]
      );
      var rot = new THREE.Euler(zone.rot[0], zone.rot[1], zone.rot[2]);
      // zone.size[0] x zone.size[1] is the ALLOWED PRINT AREA; the artwork is
      // fitted inside it preserving its own aspect ratio. The old code just
      // multiplied the zone height by the aspect and kept the zone width, so
      // any zone that wasn't square distorted the logo -- a square logo came
      // out letterboxed on the neck (0.075 x 0.045) and stretched tall on the
      // back. Nobody wants their logo stretched; the print area's shape now
      // caps the size instead of deforming the artwork.
      var artAspect = aspect || 1; // height / width
      var w = zone.size[0];
      var h = w * artAspect;
      if (h > zone.size[1]) { h = zone.size[1]; w = h / artAspect; }
      var size = new THREE.Vector3(w, h, zone.size[2]);

      var isSurfacePlane = zone.render === 'plane';

      // DecalGeometry bakes vertex positions using mesh.matrixWorld at
      // construction time. The reference prototype computed decals right
      // after setting the mesh's scale/position but before any render
      // pass -- at that instant matrixWorld is still identity, so the
      // baked geometry ends up in the mesh's own *local* space, which
      // then scales/positions correctly once added as a child of the
      // mesh. Forcing identity here reproduces that deliberately, rather
      // than depending on being called at exactly the right frame.
      var geometry;
      if (isSurfacePlane) {
        var outward = new THREE.Vector3(0, 0, 1).applyEuler(rot);
        pos.addScaledVector(outward, zone.surfaceOffset || 0.008);
        geometry = new THREE.PlaneGeometry(w, h);
      } else {
        var savedMatrixWorld = s.shirtMesh.matrixWorld.clone();
        s.shirtMesh.matrixWorld.identity();
        try {
          geometry = new DecalGeometry(s.shirtMesh, pos, rot, size);
        } catch (e) {
          s.shirtMesh.matrixWorld.copy(savedMatrixWorld);
          window.console && console.warn('FFC 3D preview: DecalGeometry threw for "' + placementId + '"', e);
          return;
        }
        s.shirtMesh.matrixWorld.copy(savedMatrixWorld);
      }

      // A zone whose projector box never actually intersects the mesh
      // surface produces valid-but-empty geometry (0 vertices) rather than
      // throwing -- that's a silent "logo doesn't appear" bug with no
      // stack trace, so flag it explicitly instead of leaving it to be
      // rediscovered by eye every time.
      var vertCount = geometry.attributes && geometry.attributes.position ? geometry.attributes.position.count : 0;
      if (!isSurfacePlane && !vertCount) {
        window.console && console.warn('FFC 3D preview: "' + placementId + '" produced an empty decal (0 vertices) -- its ZONES position/rotation/size in ffc-personalisation-3d.js does not intersect the mesh surface. Needs recalibration.');
      }

      var loader = new THREE.TextureLoader();
      var shirtAtLoadTime = s.shirtMesh;
      loader.load(imageUrl, function (tex) {
        // Guard: shopper may have removed this placement (or closed/reset
        // the panel) while the texture was still in flight.
        if (s.shirtMesh !== shirtAtLoadTime || !s.decals) return;
        tex.colorSpace = THREE.SRGBColorSpace;
        var material = new THREE.MeshBasicMaterial({
          map: tex, transparent: true, depthTest: true, depthWrite: false,
          polygonOffset: true, polygonOffsetFactor: -4, side: THREE.DoubleSide
        });
        var mesh = new THREE.Mesh(geometry, material);
        if (isSurfacePlane) {
          mesh.position.copy(pos);
          mesh.rotation.copy(rot);
        }
        mesh.renderOrder = 8;
        s.shirtMesh.add(mesh);
        // Another showDecal for the same id may have landed first (rapid
        // nudge clicks) -- drop whichever texture resolves second rather
        // than stacking duplicate decals.
        if (s.decals[placementId]) clearDecal(placementId);
        s.decals[placementId] = mesh;
      });
    },
    hideDecal: function (id) { clearDecal(id); },
    clearAllDecals: function () {
      Object.keys(s.decals).forEach(clearDecal);
    }
  };

  window.FFCPersonalisation3D = API;
})();
